/**
 * Strip quoted/reply content from email body for cleaner display in thread view.
 * In thread view, each message is shown as a separate bubble, so we only need
 * the "new" content - not the nested quoted replies.
 */

// Patterns that indicate start of quoted content (stop here)
// [^:]+ before "wrote" allows Name <email> format
const QUOTE_START_PATTERNS = [
    // "On Sat, 10 Jan 2026 at 3:00 PM, Name <email> wrote:"
    /On\s+\w+,[^:]+wrote\s*:/i,
    // "Reply N On ..." (Gmail-style) - optional "Reply" + optional number, then "On ... wrote:"
    /Reply\s*\d*\s*On\s+\w+,[^:]+wrote\s*:/i,
    // "--- Previous messages ---" (our format)
    /---\s*Previous\s+messages\s*---/i,
    // "---------- Forwarded message ---------"
    /-{3,}\s*Forwarded\s+message\s*-{3,}/i,
];

/**
 * Strip quoted content from plain text email body.
 * Returns only the "new" message content.
 */
export function stripQuotedContentPlainText(text) {
    if (!text || typeof text !== 'string') return text;
    const lines = text.split('\n');
    const result = [];
    for (let i = 0; i < lines.length; i++) {
        let line = lines[i];
        const trimmed = line.trim();
        // Check for quote start patterns (full line)
        let foundQuote = false;
        let truncateAt = -1;
        for (const pat of QUOTE_START_PATTERNS) {
            const m = trimmed.match(pat);
            if (m) {
                foundQuote = true;
                truncateAt = trimmed.indexOf(m[0]);
                break;
            }
        }
        if (foundQuote) {
            if (truncateAt > 0) {
                const before = trimmed.slice(0, truncateAt).trim();
                if (before) result.push(before);
            }
            break;
        }
        // Line starting with > is quoted
        if (trimmed.startsWith('>')) {
            if (result.length > 0) break;
            continue;
        }
        // Check if line contains "On ... wrote:" - truncate at that point (quote within same line)
        const onWroteMatch = trimmed.match(/On\s+\w+[\s\S]*?wrote\s*:/i);
        if (onWroteMatch) {
            const idx = trimmed.indexOf(onWroteMatch[0]);
            if (idx > 0) {
                const before = trimmed.slice(0, idx).trim();
                if (before) result.push(before);
            }
            break;
        }
        result.push(line);
    }
    return result.join('\n').trim();
}

/**
 * Strip quoted content from HTML email body.
 */
export function stripQuotedContentHtml(html) {
    if (!html || typeof html !== 'string') return html;
    let out = html;
    // Remove blockquotes (including nested) via DOM parsing
    try {
        const doc = new DOMParser().parseFromString(out, 'text/html');
        doc.querySelectorAll('blockquote').forEach((el) => el.remove());
        out = doc.body?.innerHTML ?? out;
    } catch {
        // Fallback: repeatedly apply regex until no matches remain
        let prev;
        do {
            prev = out;
            out = out.replace(/<blockquote[^>]*>[\s\S]*?<\/blockquote>/gi, '');
        } while (prev !== out);
    }
    // Split at "On ... wrote:" - keep only content before
    const onWroteMatch = out.match(/On\s+\w+[\s\S]*?wrote\s*:/i);
    if (onWroteMatch) {
        const idx = out.indexOf(onWroteMatch[0]);
        if (idx > 0) out = out.slice(0, idx);
    }
    // Split at "Reply N On"
    const replyMatch = out.match(/Reply\s+\d+\s+On\s+/i);
    if (replyMatch) {
        const idx = out.indexOf(replyMatch[0]);
        if (idx > 0) out = out.slice(0, idx);
    }
    // Split at "--- Previous messages ---"
    const prevMatch = out.match(/---\s*Previous\s+messages\s*---/i);
    if (prevMatch) {
        const idx = out.indexOf(prevMatch[0]);
        if (idx > 0) out = out.slice(0, idx);
    }
    return out.replace(/\s+$/, '').trim();
}

/**
 * Strip quoted content from email body (auto-detect HTML vs plain text).
 */
export function stripQuotedContent(body) {
    if (!body || typeof body !== 'string') return body;
    const hasHtml = /<[a-z][\s\S]*>/i.test(body);
    return hasHtml ? stripQuotedContentHtml(body) : stripQuotedContentPlainText(body);
}

/**
 * Split email body into new content and quoted content for collapsible display.
 * Returns { newContent, quotedContent } - quotedContent may be empty.
 */
export function splitQuotedContent(body) {
    if (!body || typeof body !== 'string') {
        return { newContent: '', quotedContent: '' };
    }
    const hasHtml = /<[a-z][\s\S]*>/i.test(body);
    if (hasHtml) {
        return splitQuotedContentHtml(body);
    }
    return splitQuotedContentPlainText(body);
}

function splitQuotedContentPlainText(text) {
    const lines = text.split('\n');
    const newLines = [];
    const quotedLines = [];
    let inQuoted = false;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        if (inQuoted) {
            quotedLines.push(line);
            continue;
        }

        // Check for quote start patterns
        let foundQuote = false;
        let truncateAt = -1;
        for (const pat of QUOTE_START_PATTERNS) {
            const m = trimmed.match(pat);
            if (m) {
                foundQuote = true;
                truncateAt = trimmed.indexOf(m[0]);
                break;
            }
        }

        if (foundQuote) {
            inQuoted = true;
            if (truncateAt > 0) {
                const before = trimmed.slice(0, truncateAt).trim();
                if (before) newLines.push(before);
                quotedLines.push(trimmed.slice(truncateAt));
            } else {
                quotedLines.push(line);
            }
            continue;
        }

        // Line starting with > (preserve leading quoted lines even when newLines is empty)
        if (trimmed.startsWith('>')) {
            inQuoted = true;
            quotedLines.push(line);
            continue;
        }

        // Check for "On ... wrote:" within same line
        const onWroteMatch = trimmed.match(/On\s+\w+[\s\S]*?wrote\s*:/i);
        if (onWroteMatch) {
            const idx = trimmed.indexOf(onWroteMatch[0]);
            inQuoted = true;
            if (idx > 0) {
                const before = trimmed.slice(0, idx).trim();
                if (before) newLines.push(before);
                quotedLines.push(trimmed.slice(idx));
            } else {
                quotedLines.push(line);
            }
            continue;
        }

        newLines.push(line);
    }

    return {
        newContent: newLines.join('\n').trim(),
        quotedContent: quotedLines.join('\n').trim(),
    };
}

function splitQuotedContentHtml(html) {
    let newPart = html;
    let quotedPart = '';

    // Find first "On ... wrote:" or "Reply N On"
    const onWroteMatch = html.match(/On\s+\w+[\s\S]*?wrote\s*:/i);
    const replyMatch = html.match(/Reply\s+\d+\s+On\s+/i);
    const prevMatch = html.match(/---\s*Previous\s+messages\s*---/i);

    let splitIdx = -1;
    const matches = [
        onWroteMatch ? html.indexOf(onWroteMatch[0]) : -1,
        replyMatch ? html.indexOf(replyMatch[0]) : -1,
        prevMatch ? html.indexOf(prevMatch[0]) : -1,
    ].filter((i) => i >= 0);

    if (matches.length > 0) {
        splitIdx = Math.min(...matches);
        newPart = html.slice(0, splitIdx).trim();
        quotedPart = html.slice(splitIdx).trim();
    }

    return { newContent: newPart, quotedContent: quotedPart };
}
